import { Button, Descriptions, Modal, Tag } from 'antd'
import type { HookAPI } from 'antd/es/modal/useModal'
import dayjs from 'dayjs'

import AtomNotice from '@/components/Atoms/AtomNotice/AtomNotice'

import {
  formatMs,
  LOG_COUNTERS,
  type LogCounterField,
  MISSING_SKU_REPORT_DELETED_TEXT,
  runStatusMap,
  runTypeLabels,
} from '../lib/stockImportsLogLabels'
import { getMissingSkuReportState } from '../lib/stockImportsMissingSku'
import {
  PRICE_CANCEL_REASON_LABELS,
  PRICE_LOG_COUNTERS,
} from '../lib/stockImportsPriceLabels'
import MissingSkuReport from '../MissingSkuReport/MissingSkuReport'
import { labelWithTooltip } from '../RequiredLabel/labelWithTooltip'
import type { StockImportLogModel } from '../types/stockImports.types'
import cn from './StockImportLogDetailsModal.module.scss'

const DATE_TIME_FORMAT = 'DD.MM.YYYY HH:mm'
const DATE_TIME_SECONDS_FORMAT = 'DD.MM.YYYY HH:mm:ss'
const EMPTY_VALUE = '—'

export interface LogSourceInfo {
  supplierName?: string
  storeName?: string
  sourceTypeLabel?: string
}

interface StockImportLogDetailsModalProps {
  log: StockImportLogModel
  source: LogSourceInfo
  modal: HookAPI
  showPrices: boolean
  onOpenConfig: (configId: string) => void
  onClose: () => void
}

const formatDate = (value: string, format: string) =>
  dayjs(value).isValid() ? dayjs(value).format(format) : EMPTY_VALUE

const formatCounter = (log: StockImportLogModel, field: LogCounterField) => {
  if (field === 'hasMissingSkuData') {
    const state = getMissingSkuReportState(log)
    if (state === 'available') return 'есть'
    return state === 'deleted' ? 'удалён' : EMPTY_VALUE
  }
  return String(log[field])
}

function StockImportLogDetailsModal({
  log,
  source,
  modal,
  showPrices,
  onOpenConfig,
  onClose,
}: StockImportLogDetailsModalProps) {
  const status = runStatusMap[log.runStatus]
  const reportState = getMissingSkuReportState(log)

  return (
    <Modal
      open
      centered
      destroyOnHidden
      title={`Загрузка от ${formatDate(log.runStartedAt, DATE_TIME_FORMAT)}`}
      width="min(1480px, calc(100vw - 32px))"
      onCancel={onClose}
      footer={
        <Button onClick={() => onOpenConfig(log.configId)}>
          Открыть настройку
        </Button>
      }
    >
      <Descriptions size="small" column={{ xs: 1, md: 2, xl: 3 }} bordered>
        <Descriptions.Item label="Статус">
          <Tag color={status.color}>{status.label}</Tag>
        </Descriptions.Item>
        <Descriptions.Item label="Режим">
          {log.isTrial ? 'Тестовый' : 'Боевой'}
        </Descriptions.Item>
        <Descriptions.Item label="Инициатор">
          {runTypeLabels[log.runType]}
        </Descriptions.Item>
        <Descriptions.Item label="Пояснение" span={3}>
          <span className={cn.reason}>{log.errorReason || EMPTY_VALUE}</span>
        </Descriptions.Item>
        <Descriptions.Item label="Начало">
          {formatDate(log.runStartedAt, DATE_TIME_SECONDS_FORMAT)}
        </Descriptions.Item>
        <Descriptions.Item label="Окончание">
          {formatDate(log.runEndedAt, DATE_TIME_SECONDS_FORMAT)}
        </Descriptions.Item>
        <Descriptions.Item label="Продолжительность">
          {formatMs(log.parsingTimeMs)}
        </Descriptions.Item>
        <Descriptions.Item label="Поставщик">
          {source.supplierName ?? EMPTY_VALUE}
        </Descriptions.Item>
        <Descriptions.Item label="Склад">
          {source.storeName ?? EMPTY_VALUE}
        </Descriptions.Item>
        <Descriptions.Item label="Тип источника">
          {source.sourceTypeLabel ?? EMPTY_VALUE}
        </Descriptions.Item>
        <Descriptions.Item label="Файл">
          {log.fileName ?? EMPTY_VALUE}
        </Descriptions.Item>
        <Descriptions.Item label="Хэш файла" span={2}>
          <span className={cn.hash}>{log.fileHash ?? EMPTY_VALUE}</span>
        </Descriptions.Item>
        {LOG_COUNTERS.map((counter) => (
          <Descriptions.Item
            key={counter.field}
            label={labelWithTooltip(counter.label, counter.hint)}
          >
            {formatCounter(log, counter.field)}
          </Descriptions.Item>
        ))}
      </Descriptions>

      {showPrices && (
        <section className={cn.report} aria-label="Цены">
          <h3 className={cn.reportTitle}>Цены</h3>
          {log.priceCancelReason && (
            <AtomNotice
              type="error"
              description={PRICE_CANCEL_REASON_LABELS[log.priceCancelReason]}
            >
              Цены не применены
            </AtomNotice>
          )}
          <Descriptions size="small" column={{ xs: 1, md: 3, xl: 6 }} bordered>
            {PRICE_LOG_COUNTERS.map((counter) => (
              <Descriptions.Item key={counter.field} label={counter.label}>
                {log[counter.field]}
              </Descriptions.Item>
            ))}
          </Descriptions>
        </section>
      )}

      {reportState !== 'none' && (
        <section className={cn.report}>
          <h3 className={cn.reportTitle}>Незагруженные строки</h3>
          {reportState === 'available' ? (
            <MissingSkuReport
              logId={log.id}
              totalMissingRows={log.missingRows}
              modal={modal}
              canSeePrices={showPrices}
            />
          ) : (
            <p className={cn.reportDeleted}>
              {MISSING_SKU_REPORT_DELETED_TEXT}
            </p>
          )}
        </section>
      )}
    </Modal>
  )
}

export default StockImportLogDetailsModal
